<template>
  <div class="fine-uploader">
    <div class="flexGrid flexGrid--no-collapse-large">
      <div class="flexGrid__item flexGrid__item--6">
        <form ref="form">
          <label
            :class="{
              'fine-uploader__box': true,
              'is-dragging': opts.dragging,
            }"
            @dragstart.prevent="onDragEnter"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent="onDragEnter"
            @drop.prevent="onDragDrop"
            @dragleave.prevent="onDragLeave"
            @dragend.prevent="onDragLeave">
            <input type="file" ref="upload" @change="onUploadDoc"
              :accept="options.options.validation.acceptFiles"
              :multiple="options.options.multiple" />
            <div class="fine-uploader__box__icon">
              <icon name="cloud-upload" width="60" height="56"></icon>
              <i18n v-if="isTouchscreen" path="uploader.tap" tag="p">
                <span place="type">{{ type == 'signature' ? 'image' : 'documents' }}</span>
              </i18n>
              <i18n v-else path="uploader.dropOrClick" tag="p">
                <span place="type">{{ type == 'signature' ? 'image' : 'documents' }}</span>
                <br place="br" />
              </i18n>
            </div>
          </label>
        </form>
      </div>
      <div class="flexGrid__item flexGrid__item--6">
        <div class="fine-uploader__image-placeholder">
          <template v-if="loading">
            <div class="loader loader--absolute">
              <div class="loader__icon"></div>
              <div class="loader__progress" v-if="!state.hideProgressBar">
                <div class="loader__progress__bar" :style="`width: ${percentWidth}%;`"></div>
              </div>
            </div>
          </template>
          <template  v-else>
            <template v-if="type === 'document'">
              <template v-if="!submittedFiles || !submittedFiles.length">
                <icon name="file-o" height="60" width="60"></icon>
              </template>
              <template v-else v-for="file in submittedFiles">
                <div class="fine-uploader__item" :key="file.id">
                  <span>{{file.name}}</span>
                  <a @click="removeFile" :data-id="file.id">remove</a>
                </div>
              </template>
            </template>
            <template v-if="type === 'signature'">
              <template v-if="!uploadedImage">
                <icon name="file-picture-o" height="60" width="60"></icon>
              </template>
              <template v-else>
                <a href="javascript:;" class="fine-uploader__remove-button" @click="removeUploadedImage">
                  <icon name="trash" height="16" width="16" class="no-center"></icon>
                  <span>{{ $t('signatureBox.clear') }}</span>
                </a>
                <img :src="`${s3URL}/signatures/${uploadedImage}`" />
              </template>
            </template>
          </template>
          <message v-if="state.error" type="error" iconName="life-ring">
            {{ state.errorMessage }}
          </message>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import FineUploader from 'fine-uploader-wrappers'
import { mapActions, mapGetters } from 'vuex'

import Message from '@/components/Message'

export default {
  name: 'fine-uploader',
  props: [
    'fieldId',
    'type',
    'options'
  ],
  components: {
    Message
  },
  data () {
    return {
      loading: false,
      uploadedImage: null,
      submittedFiles: [],
      state: {
        bytesUploaded: null,
        hidden: true,
        hideProgressBar: false,
        totalSize: null,
        error: false,
        errorMessage: null
      },
      opts: {
        dragging: false
      }
    }
  },
  mounted () {
    if (this.$store.state.uploaders[this.fieldId]) {
      this.Uploader = this.$store.state.uploaders[this.fieldId]
    } else {
      this.Uploader = new FineUploader(this.options)
    }
    this.Uploader.on('complete', this.uploadComplete.bind(this))
    this.Uploader.on('totalProgress', this.trackProgressEventHandler.bind(this))
    this.Uploader.on('statusChange', this.trackStatusEventHandler.bind(this))
    this.Uploader.on('deleteComplete', this.onDeleteComplete.bind(this))
    this.Uploader.on('error', this.handleError.bind(this))

    this.$store.dispatch('setUploader', { uploader: this.Uploader, fieldId: this.$store.state.activeFieldId })

    if (this.type === 'signature') {
      const currentFieldUpload = this.getFieldProperty(this.fieldId, 'upload')

      if (currentFieldUpload) {
        this.uploadedImage = currentFieldUpload
      } else if (this.latestSignature.upload) {
        this.uploadedImage = this.latestSignature.upload
      }
    }

    this.submittedFiles = this.getFieldProperty(this.fieldId, 'upload') || []
  },
  computed: {
    ...mapGetters(['getField', 'getFieldProperty', 'latestSignature']),
    percentWidth () {
      return parseInt((this.state.bytesUploaded / this.state.totalSize) * 100, 10) || 0
    },
    currentField () {
      return this.getField(this.fieldId)
    },
    isTouchscreen () {
      return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)
    },
    s3URL () {
      return process.env.VUE_APP_AWS_S3_URL
    }
  },
  methods: {
    ...mapActions(['setLatestSignature', 'setCurrentFieldVal']),

    removeUploadedImage () {
      if (this.uploadedImageId != null) {
        this.Uploader.methods.deleteFile(this.uploadedImageId)
      }

      this.uploadedImage = null
      this.uploadedImageId = null

      this.setLatestSignature({ property: 'upload', value: null })
    },
    trackProgressEventHandler (bytesUploaded, totalSize) {
      this.state.bytesUploaded = bytesUploaded
      this.state.totalSize = totalSize
    },
    trackStatusEventHandler (id, prevState, currentState) {
      if (this.isUploadComplete()) {
        this.loading = false
        this.state.bytesUploaded = null
        this.state.totalSize = null
        if (this.$refs.form) this.$refs.form.reset()
      } else if (
        this.state.bytesUploaded !== null &&
          this.state.totalSize !== null &&
          currentState !== 'rejected'
      ) {
        this.loading = true
      }
    },
    onUploadDoc (event) {
      const files = event.target.files

      this.loading = true

      if (this.type === 'signature') {
        this.Uploader.methods.reset()
      }

      this.Uploader.methods.addFiles(files)
    },
    uploadComplete (id, name, response) {
      this.loading = false
      if (response.success) {
        this.state.error = false
        const inputId = this.$store.state.activeFieldId

        // TODO: WUFD - Delete "if" block after file uploader migration is completed
        if (this.type === 'document') {
          const encondedName = encodeURIComponent(name)
          const encodedUUID = encodeURIComponent(response.uuid)
          const route = this.$store.getters.isWidget ? '/upload/link-file-to-widget-field' : '/upload/link-file-to-field'
          axios({
            method: 'post',
            baseURL: process.env.VUE_APP_API_URL,
            url: route,
            data: {
              input_id: inputId,
              uuid: encodedUUID,
              original_file_name: encondedName
            }
          })
          this.submittedFiles.push({
            name,
            id,
            uuid: response.uuid
          })
          const val = new Array(...this.submittedFiles)
          this.setCurrentFieldVal({ id: inputId, val, property: 'upload' })
        }

        // TODO: WUSD - Delete "if" block after signature uploader migration is completed
        if (this.type === 'signature') {
          this.uploadedImage = response.fileName
          this.uploadedImageId = id

          this.setLatestSignature({ property: 'upload', value: this.uploadedImage })
        }
      }
    },
    isUploadComplete () {
      return this.state.bytesUploaded === this.state.totalSize
    },
    removeFile (event) {
      const id = event.currentTarget.getAttribute('data-id')
      this.Uploader.methods.deleteFile(parseInt(id, 10))
      this.loading = false
      this.state.hideProgressBar = true
    },
    onDeleteComplete (id, response, isError) {
      if (!isError) {
        const inputId = this.$store.state.activeFieldId

        let fileIndex = 0
        this.submittedFiles.forEach((file, index) => {
          if (id === file.id) {
            fileIndex = index
          }
        })
        this.submittedFiles.splice(fileIndex, 1)

        if (this.type === 'document') {
          const val = new Array(...this.submittedFiles)
          this.setCurrentFieldVal({ id: inputId, val, property: 'upload' })
        }

        this.loading = false
        this.state.hideProgressBar = false
        this.opts.dragging = false
      }
    },
    handleError (id, name, error) {
      // Restricted file name upto 70 character
      if (name.length > 70) error = 'The filename should not be greater than 70 characters.'
      this.state.error = true
      this.state.errorMessage = error
      this.loading = false
      if (this.$refs.form) this.$refs.form.reset()
      setTimeout(() => {
        this.state.error = false
      }, 2250)
    },
    onDragEnter () {
      this.opts.dragging = true
    },
    onDragLeave () {
      this.opts.dragging = false
    },
    onDragDrop ($event) {
      const files = $event.dataTransfer.files
      this.loading = true
      this.opts.dragging = false
      if (this.type === 'signature') {
        this.Uploader.methods.reset()
      }
      this.Uploader.methods.addFiles(files)
    }
  }
}
</script>

<style lang="sass" scoped>
  @import '@/styles/component-utils.sass'
  @import '@/styles/_elements.loader.sass'

  .fine-uploader
    &__box
      display: block
      border: 3px dashed $color-border-dark
      padding: $spacing-base
      min-height: 250px
      position: relative
      cursor: pointer
      &.is-dragging
        border-color: $color-blue
        .fine-uploader__box__icon
          svg
            fill: $color-blue
          p
            color: $color-blue
      &__icon
        position: absolute
        top: 50%
        left: 50%
        margin-right: 0
        margin-left: -150px
        margin-top: -50px
        text-align: center
        width: 300px
        height: 100px
        line-height: 1.5
      svg
        fill: $color-border-dark
      p
        margin: 0
        color: $color-border-dark
        font-weight: bold
      input
        display: none
    &__image-placeholder
      border: 1px solid $color-border-dark
      position: relative
      min-height: 250px
      max-height: 250px
      overflow: auto
      padding: $spacing-xs
      svg:not(.no-center)
        margin-right: 0
        position: absolute
        left: 50%
        top: 50%
        margin-left: -30px
        margin-top: -30px
      img
        position: absolute
        top: 50%
        transform: translate3d(0, -50%, 0)
    &__item
      background: $signable-secondary-4-shade-2
      padding: $spacing-small
      margin-top: $spacing-xs
      @include clearfix
      &:first-child
        margin-top: 0
      span
        font-weight: bold
      a
        float: right
        text-decoration: underline
        color: $color-warning
        cursor: pointer
    &__remove-button
      position: absolute
      top: $spacing-base
      right: $spacing-base
      background: $color-cancel
      padding: $spacing-small
      color: #fff
      border-radius: 3px
      text-decoration: none
      z-index: 10
      box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.15)
      svg
        float: left
    // components
    .message
      position: relative
      top: $spacing-base
      left: 50%
      transform: translate3d(-50%, 0, 0)
      box-shadow: 2px 5px 10px -5px #000

  // Responsive
  @media screen and (max-width: $screen-size-xs)
    .fine-uploader
      &__box
        margin-bottom: $spacing-base
      &__box,
      &__image-placeholder
        min-height: 150px
        max-height: 150px
      &__item
        a
          margin-top: $spacing-xs
  @media all and (max-width: $screen-size-medium) and (orientation : landscape)
    .fine-uploader
      &__box
        margin-bottom: $spacing-base
      &__box,
      &__image-placeholder
        min-height: 150px
        max-height: 150px
      &__item
        a
          margin-top: $spacing-xs
  @media screen and (max-width: $screen-size-medium)
    // components
    .message
      width: 95%
</style>
